import {
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useMenu, UseMenuProps } from "../../../../hook/menu";
import { useInsertHttpHookBlock } from "../../../../hook/page/block/httpHook";
import { ComponentSelectMenu } from "../../component";
import { ProtocolSelectMenu } from "../../protocol";
import { Protocol, ProtocolSpec } from "../../../../types/protocol";
import { Component } from "../../../../types/component";

const getArity2Methods = (proto: Protocol): string[] => {
  try {
    const spec: ProtocolSpec = JSON.parse(proto.spec);
    return spec.functions
      .filter((f) => f.args.length === 2)
      .map((f) => f.name);
  } catch {
    return [];
  }
};

export const AddHttpHookMenu: React.FC<
  UseMenuProps & { blockIndex: number }
> = ({ blockIndex, ...useMenuProps }) => {
  const { onClose } = useMenuProps;
  const [formState, setFormState] = useState<{
    selectedProto: Protocol | undefined;
    selectedComp: Component | undefined;
    selectedMethod: string | undefined;
    protoRequired: boolean;
    compRequired: boolean;
    methodRequired: boolean;
  }>({
    selectedProto: undefined,
    selectedComp: undefined,
    selectedMethod: undefined,
    protoRequired: false,
    compRequired: false,
    methodRequired: false,
  });
  const { selectedProto, selectedComp, selectedMethod, compRequired, protoRequired, methodRequired } = formState;
  const insertHttpHookBlock = useInsertHttpHookBlock();

  const arity2Methods = useMemo(
    () => (selectedProto ? getArity2Methods(selectedProto) : []),
    [selectedProto],
  );

  const handleSubmit = async () => {
    const compMissing = !selectedComp;
    const protoMissing = !selectedProto;
    const methodMissing = !selectedMethod;
    setFormState((s) => ({ ...s, compRequired: compMissing, protoRequired: protoMissing, methodRequired: methodMissing }));
    if (compMissing || protoMissing || methodMissing) return;
    await insertHttpHookBlock({
      blockIndex,
      pageId: selectedComp!["page-id"],
      protoName: selectedProto!.name,
      methodName: selectedMethod!,
    });
    onClose();
  };

  const { anchorEl: anchorElComp, handleOpen: handleOpenComp, onClose: onCloseComp, open: openComp } = useMenu();
  const { anchorEl: anchorElProto, handleOpen: handleOpenProto, onClose: onCloseProto, open: openProto } = useMenu();

  return (
    <>
      <ComponentSelectMenu
        anchorEl={anchorElComp}
        onChange={async (comp) => setFormState((s) => ({ ...s, selectedComp: comp }))}
        onClose={onCloseComp}
        open={openComp}
      />
      <ProtocolSelectMenu
        anchorEl={anchorElProto}
        onChange={async (proto) => setFormState((s) => ({ ...s, selectedProto: proto, selectedMethod: undefined }))}
        onClose={onCloseProto}
        open={openProto}
      />
      <Menu {...useMenuProps}>
        <Box sx={{ width: "20rem", padding: 3, gap: 2, display: "flex", flexDirection: "column" }}>
          <Typography variant="h5">Add HTTP Hook</Typography>
          <Typography variant="subtitle1">Register an HTTP endpoint wired to a component method</Typography>
          <Divider />
          <FormControl>
            <FormLabel>Component (Handler Page)</FormLabel>
            <Button onClick={handleOpenComp}>{selectedComp?.name ?? "Select Component"}</Button>
            {compRequired && <FormHelperText error>Component required</FormHelperText>}
          </FormControl>
          <FormControl>
            <FormLabel>Protocol</FormLabel>
            <Button onClick={handleOpenProto}>{selectedProto?.name ?? "Select Protocol"}</Button>
            {protoRequired && <FormHelperText error>Protocol required</FormHelperText>}
          </FormControl>
          {selectedProto && (
            <FormControl>
              <FormLabel>Method (arity 2: self, Input)</FormLabel>
              {arity2Methods.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No arity-2 methods on this protocol</Typography>
              ) : (
                <List dense>
                  {arity2Methods.map((name) => (
                    <ListItemButton
                      key={name}
                      selected={selectedMethod === name}
                      onClick={() => setFormState((s) => ({ ...s, selectedMethod: name }))}
                    >
                      <ListItemText primary={name} />
                    </ListItemButton>
                  ))}
                </List>
              )}
              {methodRequired && <FormHelperText error>Method required</FormHelperText>}
            </FormControl>
          )}
          <Button onClick={handleSubmit} variant="outlined">Add HTTP Hook</Button>
        </Box>
      </Menu>
    </>
  );
};
